// Home.jsx
import { useState, useEffect, useCallback } from "react";
import About from "./About";
import Navix from "./Navix/Navix";
import Location from "./Location";
import MapComponent from "./Map/MapComponent";
import Keyword from "./Keyword";
import AISearch from "./AI";
import Help from "./Help";
import SearchHub from "./SearchHub";
import MapExtComponent from "./Map/MapExtComponent";
import FinderComponent from "./Finder/FinderComponent";
import {
  RiLoginCircleLine,
  RiLogoutCircleLine,
  RiFeedbackLine,
} from "react-icons/ri";
import keycloak, {
  loginUser,
  logoutUser,
  initializeKeycloak,
} from "../keycloak";
import FeedbackForm from "./FeedbackForm";

export default function Home() {
  const [activeComponent, setActiveComponent] = useState("counties");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [mapStyle, setMapStyle] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const handleSearch = (component) => {
    setActiveComponent(component);
  };

  const renderComponent = () => {
    switch (activeComponent) {
      case "counties":
        return (
          <Navix
            isAuthenticated={isAuthenticated}
            onSearch={handleSearch}
            token={token}
          />
        );
      case "map":
        return <MapComponent onFetchData={handleDataFetch} />;
      case "query":
        return (
          <MapExtComponent
            token={token}
            key="map-ext-component"
            data={data}
            error={error}
            path="/location/search"
            ssp={true}
            mapStyleProp={mapStyle}
            onBack={() => setActiveComponent("map")}
          />
        );
      case "location":
        return <Location token={token} />;
      case "keyword":
        return <Keyword token={token} />;
      case "ai":
        return <AISearch token={token} />;
      case "datasets":
        return <FinderComponent token={token} />;
      case "help":
        return <Help />;
      case "about":
      default:
        return (
          <About isAuthenticated={isAuthenticated} onSearch={handleSearch} />
        );
    }
  };

  const handleDataFetch = useCallback((fetchedData, selectedMapStyle) => {
    if (fetchedData) {
      setData(fetchedData);
      setMapStyle(selectedMapStyle);
      setError(null);
    } else {
      setData(null);
      setError("No matching results found. Please refine your search.");
    }
    setActiveComponent("query");
  }, []);

  const handleSidebarCollapse = (isCollapsed) => {
    setSidebarCollapsed(isCollapsed);
  };

  const handleAuthChange = (authStatus) => {
    setIsAuthenticated(authStatus);
    setToken(authStatus ? keycloak.token : null);
  };

  const toggleAuth = () => {
    if (isAuthenticated) {
      logoutUser();
      setIsAuthenticated(false);
      setToken(null);
    } else {
      loginUser();
    }
  };

  const handleFeedbackClick = () => {
    setIsFeedbackOpen(true);
  };

  useEffect(() => {
    initializeKeycloak(handleAuthChange);
  }, []);

  return (
    <div className="layout-container">
      <div className="top-bar">
        <div className="logo-container">
          <img src="assets/tdislogo-white.png" alt="TDIS Logo" />
          <img src="assets/glowhite.png" alt="GLO Logo" />
        </div>
        <div className="app-title-container">
          <h1 className="app-title">
            <span className="full-text">
              <span className="title-word">
                <span className="highlight">D</span>ATA
              </span>
              <span className="separator"></span>
              <span className="separator">&amp;</span>
              <span className="separator"></span>
              <span className="title-word">
                <span className="highlight">M</span>ODELS
              </span>
              <span className="separator"></span>
              <span className="title-word">
                <span className="highlight">Q</span>UERY
              </span>
              <span className="separator"></span>
              <span className="title-word">
                <span className="highlight">T</span>OOL
              </span>
            </span>
            <span className="short-text" title="Data & Models Query Tool">
              DMQT
            </span>
          </h1>
        </div>
        <div className="header-right">
          <button
            className={`header-auth-button ${
              isAuthenticated ? "authenticated" : ""
            }`}
            onClick={toggleAuth}
            title={isAuthenticated ? "Click to logout" : "Click to login"}
          >
            {isAuthenticated ? (
              <>
                <RiLogoutCircleLine /> Logout
              </>
            ) : (
              <>
                <RiLoginCircleLine /> Login
              </>
            )}
          </button>
          <button
            className="header-feedback-button"
            onClick={handleFeedbackClick}
            title="Report Feedback"
          >
            <RiFeedbackLine /> Feedback
          </button>
          <img
            className="right-logo"
            src="assets/tamulogo-white.png"
            alt="TAMU Logo"
          />
        </div>
      </div>
      <div className="main-content">
        <SearchHub
          onSearch={handleSearch}
          collapsed={sidebarCollapsed}
          onCollapse={handleSidebarCollapse}
          isAuthenticated={isAuthenticated}
          token={token}
          activeComponent={activeComponent}
        />
        <div
          className={`content-area ${
            sidebarCollapsed ? "sidebar-collapsed" : "sidebar-expanded"
          }`}
        >
          {renderComponent()}
        </div>
      </div>
      <div className="footer">
        <span className="footer-text">TDIS © All rights reserved</span>
        <img
          className="footer-logo"
          src="assets/tamulogo-white.png"
          alt="TAMU"
        />
      </div>
      <FeedbackForm
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        userEmail={isAuthenticated ? keycloak.tokenParsed?.email : ""}
      />
    </div>
  );
}
